import { useEffect } from 'react';

import { App, Col, Result, Row } from 'antd';
import { useTranslation } from 'react-i18next';

import Layout from 'components/Layout/Layout';

import { useAuthContext } from 'hooks/useAuthContext';

import DashboardDonutChart from './components/DashboardDonutChart';
import DashboardFilters from './components/DashboardFilters';
import DashboardGrid from './components/DashboardGrid';
import DashboardKpiCards from './components/DashboardKpiCards';
import DashboardStackedBarChart from './components/DashboardStackedBarChart';
import { useDashboardData } from './hooks/useDashboardData';
import { useDashboardFilters } from './hooks/useDashboardFilters';
import { useDashboardMetadata } from './hooks/useDashboardMetadata';

import './DashboardAlternativeMode.scss';

const BASE = 'scenes.kit.dashboard_alternative_mode';

function DashboardAlternativeMode() {
  const { message } = App.useApp();
  const { t } = useTranslation();
  const { user } = useAuthContext();

  const metadata = useDashboardMetadata({ message });
  const dashboardData = useDashboardData({ message });
  const filters = useDashboardFilters((dateRange, campaignIds, weekDays) => {
    dashboardData.fetchDashboardData(dateRange, campaignIds, weekDays);
  });

  useEffect(() => {
    if (user.company.kitIntegrationEnabled) {
      dashboardData.fetchDashboardData(
        filters.dateRange,
        filters.selectedCampaignIds,
        filters.selectedWeekDays
      );
    }
  }, []);

  if (!user.company.kitIntegrationEnabled) {
    return (
      <Layout>
        <Result
          status="403"
          title="403"
          subTitle={t(`${BASE}.not_authorized`)}
        />
      </Layout>
    );
  }

  return (
    <Layout breadcrumbMode="none">
      <div className="kit-dashboard-alt">
        <DashboardFilters
          filters={filters}
          campaigns={metadata.campaigns}
          metadataLoading={metadata.metadataLoading}
          loading={dashboardData.dataLoading}
          user={user}
        />

        <DashboardKpiCards
          data={dashboardData.aggregateSection}
          loading={dashboardData.dataLoading}
          user={user}
        />

        <Row gutter={[16, 16]} className="kit-dashboard-alt__charts-row">
          <Col xs={24} lg={8}>
            <DashboardDonutChart
              data={dashboardData.turnCountByTask}
              loading={dashboardData.dataLoading}
            />
          </Col>
          <Col xs={24} lg={16}>
            <DashboardStackedBarChart
              data={dashboardData.turnProgressByDay}
              loading={dashboardData.dataLoading}
            />
          </Col>
        </Row>

        <DashboardGrid
          data={dashboardData.turnSummaryStatusGrid}
          loading={dashboardData.dataLoading}
          touchLatamApiUrl={metadata.touchLatamApiUrl}
        />
      </div>
    </Layout>
  );
}

export default DashboardAlternativeMode;
